// src/components/resources/calculators/video-dropoff/utils/calculations.ts

/**
 * @file Calculation Utilities
 * @description Core calculation functions for video dropoff metrics
 */

import type { CalculatorInputs, CalculatorResults, RetentionRateItem, DropoffRateItem } from '~/components/resources/calculators/video-dropoff/types'

/**
 * Calculates retention rates, drop-off rates, and advanced metrics based on user inputs.
 *
 * @param {CalculatorInputs} inputs - The user-provided calculator inputs, including platform, video length, total views, and breakpoints.
 * @returns {CalculatorResults} An object containing calculated retention rates, drop-off rates, and advanced metrics.
 */
export const calculateMetrics = (inputs: CalculatorInputs): CalculatorResults => {
  // Sort breakpoints by value (time or percentile)
  const sortedBreakpoints = [...inputs.breakpoints].sort((a, b) => {
    // Convert percentile to absolute time for comparison
    const aValue = a.isPercentile ? (a.value / 100) * inputs.videoLength : a.value
    const bValue = b.isPercentile ? (b.value / 100) * inputs.videoLength : b.value
    return aValue - bValue
  })

  // Calculate retention rates
  const retentionRates: RetentionRateItem[] = sortedBreakpoints.map(breakpoint => ({
    breakpoint,
    value: (breakpoint.viewCount / inputs.totalViews) * 100,
    absoluteValue: breakpoint.viewCount
  }))

  // Calculate drop-off rates
  const dropoffRates: DropoffRateItem[] = sortedBreakpoints.map((breakpoint, index) => {
    if (index === 0) {
      // First breakpoint (usually video start)
      return {
        breakpoint,
        value: 0, // No drop-off at start
        absoluteValue: 0
      }
    } else {
      const previousBreakpoint = sortedBreakpoints[index - 1]
      // Ensure previous breakpoint exists
      if (!previousBreakpoint) {
        return {
          breakpoint,
          value: 0,
          absoluteValue: 0
        }
      }
      
      const previousViewCount = previousBreakpoint.viewCount
      const currentViewCount = breakpoint.viewCount
      const viewersLost = Math.max(0, previousViewCount - currentViewCount)
      const dropoffRate = (viewersLost / previousViewCount) * 100

      return {
        breakpoint,
        value: dropoffRate,
        absoluteValue: viewersLost
      }
    }
  })

  // Calculate advanced metrics
  // 1. Per-second drop-off rate
  let totalSecondDrop = 0
  let totalSecondWeight = 0

  for (let i = 1; i < sortedBreakpoints.length; i++) {
    const current = sortedBreakpoints[i]
    const previous = sortedBreakpoints[i - 1]
    
    if (!current || !previous) continue
    
    // Calculate time difference between breakpoints
    const prevTimeInSeconds = previous.isPercentile 
      ? (previous.value / 100) * inputs.videoLength 
      : previous.value
    
    const currTimeInSeconds = current.isPercentile
      ? (current.value / 100) * inputs.videoLength
      : current.value
    
    const timeDiffSeconds = Math.max(1, currTimeInSeconds - prevTimeInSeconds)
    
    // Calculate viewers lost per second
    const viewersLost = previous.viewCount - current.viewCount
    const viewersLostPerSecond = viewersLost / timeDiffSeconds
    const perSecondDropRate = (viewersLostPerSecond / previous.viewCount) * 100
    
    // Weighted average based on segment duration
    totalSecondDrop += perSecondDropRate * timeDiffSeconds
    totalSecondWeight += timeDiffSeconds
  }

  const perSecondDropoff = totalSecondWeight > 0 
    ? totalSecondDrop / totalSecondWeight 
    : 0

  // 2. Average view duration
  let totalViewDuration = 0

  for (let i = 0; i < sortedBreakpoints.length - 1; i++) {
    const current = sortedBreakpoints[i]
    const next = sortedBreakpoints[i + 1]
    
    if (!current || !next) continue
    
    // Calculate time for this segment
    const currentTimeInSeconds = current.isPercentile
      ? (current.value / 100) * inputs.videoLength
      : current.value
    
    const nextTimeInSeconds = next.isPercentile
      ? (next.value / 100) * inputs.videoLength
      : next.value
    
    const segmentDuration = nextTimeInSeconds - currentTimeInSeconds
    const viewersInSegment = current.viewCount - next.viewCount
    
    // Estimate average drop-off point - assume uniform distribution in segment
    const avgTimeInSegment = currentTimeInSeconds + (segmentDuration / 2)
    
    // Add to total view duration
    totalViewDuration += viewersInSegment * avgTimeInSegment
  }

  // Add duration for viewers who watched the entire video
  const lastBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1]
  if (lastBreakpoint) {
    totalViewDuration += lastBreakpoint.viewCount * inputs.videoLength
  }

  const averageViewDuration = inputs.totalViews > 0
    ? totalViewDuration / inputs.totalViews
    : 0

  // 3. Completion rate
  const completionRate = inputs.totalViews > 0 && lastBreakpoint
    ? (lastBreakpoint.viewCount / inputs.totalViews) * 100
    : 0

  return {
    retentionRates,
    dropoffRates,
    advancedMetrics: {
      perSecondDropoff,
      averageViewDuration,
      completionRate
    }
  }
}
